import React, { useState } from 'react';
import { Box, Grow, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { OrderId } from '../../../../types/order';
import CP67BoxDoor from './CP67BoxDoor';
import KoluluBoxInfoBox from '../KolulaBoxInfoBox';
// import Assets from '../../../../assets/assets';

// Styled components using MUI v5's `styled`
const Root = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    width: '240px',
    height: '302px',
    margin: '0 auto',
    padding: '0px',
    boxSizing: 'border-box',
    position: 'relative',
    borderRadius: '8px',
    border: '1px solid black',
    overflow: 'hidden', // Add overflow for scrollbars
    transform: 'scale(135%) translateY(45px)',
}));

const DoorWrapper = styled(Box)({
    position: 'relative',
});

const Rectangle = styled(Box)(({ theme }) => ({
    width: '243px',
    height: '30px',
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const Title = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '13px',
    fontWeight: 'bold',
    marginLeft: '10px',
    // Let long station names wrap inside the header instead of pushing
    // into the info icon (#1035 item 2).
    paddingRight: '8px',
    lineHeight: 1.15,
    overflowWrap: 'anywhere',
}));

const InfoIconButton = styled(IconButton)({
    width: '30px',
    height: '30px',
    flexShrink: 0,
});

const ShadowOverlay = styled(Box)({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 9,
});

const InfoBoxWrapper = styled(Box)({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 10,
});

interface CP67BoxProps {
    orderId?: OrderId;
    activeDoors: number[];
    title: string;
}

const CP67Box = (props: CP67BoxProps) => {
    // const InfoIcon = Assets.icon.infoIcon.component;
    const [infoOpen, setInfoOpen] = useState<boolean>(false);
    const activeDoor = props.activeDoors[0];
    const enableInfo = Boolean(props.orderId);

    const getLeftSize = (columnOrder: number) => {
        if (columnOrder === 1) {
            return 0;
        } else if (columnOrder === 2) {
            return 60;
        } else if (columnOrder === 3) {
            return 120;
        } else {
            return 180;
        }
    };

    const renderColumnWithCU = (
        activeDoor: number, 
        columnOrder: number, 
        size: number, 
        doorsHeight: number, 
        firstDoorNumber: number,
        cuHeight: number,
        cuNumber: number,
    ) => {
        const leftSize = getLeftSize(columnOrder);
        const doors = [];
        let topOffset = 32;
        let doorCounter = firstDoorNumber;

        for (let i = 0; i < size; i++) {
            if (i === cuNumber - 1) {
                // Control Unit - no number, gray background
                doors.push({ top: topOffset, height: cuHeight, doorNumber: 0, isCU: true });
                topOffset += cuHeight;
            } else {
                doors.push({ top: topOffset, height: doorsHeight, doorNumber: doorCounter, isCU: false });
                topOffset += doorsHeight;
                doorCounter++;
            }
        }

        return doors.map((door, index) => (
            <CP67BoxDoor
                key={`${door.doorNumber}-${index}`}
                top={door.top}
                left={leftSize ?? 0}
                height={door.height}
                doorNumber={door.doorNumber}
                isCU={door.isCU}
                active={
                    !door.isCU && 
                    ((activeDoor - firstDoorNumber < cuNumber) && (activeDoor === door.doorNumber)) 
                        ? 
                        true 
                        :
                        ((activeDoor - firstDoorNumber >= cuNumber - 1) 
                        && (activeDoor === door.doorNumber)) 
                            ? true 
                            : false
                }
            />
        ));
    };

    const renderColumn = (
        activeDoor: number, 
        columnOrder: number, 
        size: number, 
        doorsHeight: number, 
        firstDoorNumber: number,
    ) => {
        const leftSize = getLeftSize(columnOrder);
        return Array.from({ length: size }, (_, index) => {
            const top = 32 + index * (doorsHeight);
            const doorNumber = firstDoorNumber + index;
            return (
                <CP67BoxDoor 
                    key={doorNumber} 
                    top={top} 
                    left={leftSize ?? 0} 
                    height={doorsHeight} 
                    doorNumber={doorNumber} 
                    active={index + 1 === activeDoor - firstDoorNumber + 1 ? true : false} 
                />
            );
        });
    };

    const renderFirstColumn = (activeDoor: number, columnOrder: number) => {
        const size = 7;        
        const doorsHeight = 34.5; 
        const firstDoorNumber = 1;
        const cuHeight = 66.3;
        const cuNumber = 2;
        return renderColumnWithCU(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber, cuHeight, cuNumber);
    };
    
    const renderSecondColumn = (activeDoor: number, columnOrder: number) => {
        const size = 12;        
        const doorsHeight = 22.75; 
        const firstDoorNumber = 8;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderThirdColumn = (activeDoor: number, columnOrder: number) => {
        const size = 8;        
        const doorsHeight = 34; 
        const firstDoorNumber = 20;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderFourthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54.25; 
        const firstDoorNumber = 28;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderFifthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54.25; 
        const firstDoorNumber = 33;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderSixthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54.25; 
        const firstDoorNumber = 38;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderSeventhColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54.25; 
        const firstDoorNumber = 43;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderEighthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54.25; 
        const firstDoorNumber = 48;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderNinthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54.25; 
        const firstDoorNumber = 53;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderTenthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54.25; 
        const firstDoorNumber = 58;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderEleventhColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54.25; 
        const firstDoorNumber = 63;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    // Determine which column contains the active door
    const getActiveColumn = (doorNumber: number): number => {
        if (doorNumber >= 1 && doorNumber <= 7) return 1;
        if (doorNumber >= 8 && doorNumber <= 19) return 2;
        if (doorNumber >= 20 && doorNumber <= 27) return 3;
        if (doorNumber >= 28 && doorNumber <= 32) return 4;
        if (doorNumber >= 33 && doorNumber <= 37) return 5;
        if (doorNumber >= 38 && doorNumber <= 42) return 6;
        if (doorNumber >= 43 && doorNumber <= 47) return 7;
        if (doorNumber >= 48 && doorNumber <= 52) return 8;
        if (doorNumber >= 53 && doorNumber <= 57) return 9;
        if (doorNumber >= 58 && doorNumber <= 62) return 10;
        if (doorNumber >= 63 && doorNumber <= 67) return 11;
        return 1; // Default to first column
    };

    // Calculate which columns to show (active, 1 before, 2 after)
    const getVisibleColumns = (activeColumn: number): number[] => {
        const totalColumns = 11;
        
        if (activeColumn <= 2) {
            // Show first 4 columns
            return [1, 2, 3, 4];
        } else if (activeColumn >= totalColumns - 1) {
            // Show last 4 columns
            return [totalColumns - 3, totalColumns - 2, totalColumns - 1, totalColumns];
        } else {
            // Show active, 1 before, 2 after
            return [activeColumn - 1, activeColumn, activeColumn + 1, activeColumn + 2];
        }
    };

    const activeColumn = getActiveColumn(activeDoor);
    const visibleColumns = getVisibleColumns(activeColumn);
    const firstVisibleColumn = visibleColumns[0];

    const renderColumnByNumber = (columnNumber: number) => {
        const activeDoorNum = props.activeDoors[0];
        // Adjust the column order to be relative to the first visible column
        const adjustedColumnOrder = columnNumber - firstVisibleColumn + 1;
        
        switch(columnNumber) {
            case 1: return renderFirstColumn(activeDoorNum, adjustedColumnOrder);
            case 2: return renderSecondColumn(activeDoorNum, adjustedColumnOrder);
            case 3: return renderThirdColumn(activeDoorNum, adjustedColumnOrder);
            case 4: return renderFourthColumn(activeDoorNum, adjustedColumnOrder);
            case 5: return renderFifthColumn(activeDoorNum, adjustedColumnOrder);
            case 6: return renderSixthColumn(activeDoorNum, adjustedColumnOrder);
            case 7: return renderSeventhColumn(activeDoorNum, adjustedColumnOrder);
            case 8: return renderEighthColumn(activeDoorNum, adjustedColumnOrder);
            case 9: return renderNinthColumn(activeDoorNum, adjustedColumnOrder);
            case 10: return renderTenthColumn(activeDoorNum, adjustedColumnOrder);
            case 11: return renderEleventhColumn(activeDoorNum, adjustedColumnOrder);
            default: return null;
        }
    };    

    return (
        <Box>
            {infoOpen && <ShadowOverlay />}
            <Root>
                <DoorWrapper>
                    <Rectangle style={{ backgroundColor: "black" }} left={0} top={0}>
                        <Title style={{ color: "white" }}>{props.title}</Title>
                        {enableInfo && (  
                            <InfoIconButton size="small" onClick={() => setInfoOpen(true)}>
                                {/* <InfoIcon /> */}
                            </InfoIconButton>                    
                        )}
                    </Rectangle>
                    {visibleColumns.map(columnNumber => renderColumnByNumber(columnNumber))}
                </DoorWrapper>
                <Grow in={infoOpen} timeout={500}>
                    <InfoBoxWrapper>
                        {/* <KoluluBoxInfoBox 
                            open={infoOpen}
                            onClose={() => setInfoOpen(false)}
                            orderId={props.orderId}
                        /> */}
                    </InfoBoxWrapper>
                </Grow>
            </Root>
        </Box>
    );
};

export default CP67Box;
